import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../axiosConfig';
import { typeIcons } from './Utils';
import './AccountSelector.css';

const AccountSelector = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const inputWrapperRef = useRef(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await axios.get('/accounts/', { withCredentials: true });
        const accounts = res.data;
        setAllAccounts(accounts);

        if (value) {
          const found = accounts.find(c => c.id === (value.id || value));
          setSelectedAccount(found || null);
          setQuery(found ? found.name_short : '');
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };

    fetchAccounts();
  }, [value]);

  useEffect(() => {
    if (query.length < 2 || selectedAccount) {
      setMatches([]);
      return;
    }

    const filtered = allAccounts.filter(account =>
      account.name_long?.toLowerCase().includes(query.toLowerCase()) ||
      account.name_short?.toLowerCase().includes(query.toLowerCase())
    );
    setMatches(filtered.slice(0, 5));
  }, [query, allAccounts, selectedAccount]);

  // Position the dropdown relative to the viewport (rather than the nearest
  // scrollable/clipping ancestor) so it can render via a portal on top of
  // everything, including modals with overflow:hidden/auto.
  useEffect(() => {
    if (matches.length === 0) {
      setDropdownRect(null);
      return;
    }

    const updateRect = () => {
      if (!inputWrapperRef.current) return;
      const rect = inputWrapperRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    };

    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [matches]);

  const handleSelect = (account) => {
    onChange(account);
    setSelectedAccount(account);
    setQuery(account.name_short);
    setMatches([]);
  };

  const clearSelection = () => {
    onChange(null);
    setSelectedAccount(null);
    setQuery('');
  };

  return (
    <div className="account-selector">
      <div className="input-wrapper" ref={inputWrapperRef}>
        <input autoComplete="off"
          id="account-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type an account name..."
          readOnly={!!selectedAccount}
        />
        {selectedAccount && (
          <button
            type="button"
            className="remove-account-btn"
            onClick={clearSelection}
          >
            ×
          </button>
        )}
      </div>

      {matches.length > 0 && dropdownRect && createPortal(
        <ul
          className="account-matches"
          style={{
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
          }}
        >
          {matches.map((account) => (
            <li key={account.id} onClick={() => handleSelect(account)} className="account-match">
              {/* Laid out like the header search's account results */}
              {typeIcons.account}
              <strong> {account.name_short}</strong>
              {account.name_long && account.name_long !== account.name_short && (
                <span> – {account.name_long}</span>
              )}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default AccountSelector;
